<template>
  <AdminLayout>
    <div class="p-6 lg:p-8">
      <!-- 페이지 헤더 -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-green-600/10 rounded-xl flex items-center justify-center">
            <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-slate-800">포인트 직접 지급</h1>
            <p class="text-sm text-slate-500">파트너에게 직접 포인트를 지급합니다</p>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- 포인트 지급 폼 -->
        <div class="bg-white rounded-xl border border-slate-200 p-6">
          <h2 class="text-lg font-bold text-slate-800 mb-4">포인트 지급</h2>

          <!-- 파트너 선택 -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-slate-700 mb-2">파트너 선택</label>
            <div class="relative">
              <input
                type="text"
                v-model="searchQuery"
                @focus="showDropdown = true"
                placeholder="파트너 이름 또는 이메일로 검색"
                class="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20 transition"
              />
              <!-- 검색 결과 드롭다운 -->
              <div
                v-if="showDropdown && filteredPartners.length > 0"
                class="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-auto"
              >
                <div
                  v-for="partner in filteredPartners"
                  :key="partner.id"
                  @click="selectPartner(partner)"
                  class="px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                >
                  <div class="flex items-center gap-2">
                    <p class="font-medium text-slate-800">{{ partner.displayName || partner.name || '이름 없음' }}</p>
                    <span v-if="partner.displayName && partner.displayName !== partner.name" class="text-xs text-slate-400">({{ partner.name }})</span>
                  </div>
                  <p class="text-sm text-slate-500">{{ partner.email }}</p>
                  <p v-if="partner.propertyName" class="text-xs text-violet-500 mt-0.5">{{ partner.propertyName }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- 선택된 파트너 정보 -->
          <div v-if="selectedPartner" class="mb-4 p-4 bg-green-50 rounded-xl border border-green-200">
            <div class="flex items-center justify-between">
              <div>
                <div class="flex items-center gap-2">
                  <p class="font-medium text-slate-800">{{ selectedPartner.displayName || selectedPartner.name || '이름 없음' }}</p>
                  <span v-if="selectedPartner.displayName && selectedPartner.displayName !== selectedPartner.name" class="text-xs text-slate-400">({{ selectedPartner.name }})</span>
                </div>
                <p class="text-sm text-slate-500">{{ selectedPartner.email }}</p>
                <p v-if="selectedPartner.propertyName" class="text-xs text-violet-500 mt-0.5">{{ selectedPartner.propertyName }}</p>
              </div>
              <div class="text-right">
                <p class="text-sm text-slate-500">현재 잔액</p>
                <p class="text-lg font-bold text-green-600">{{ selectedPartnerPoint?.balance?.toLocaleString() || 0 }}P</p>
              </div>
            </div>
            <button
              @click="clearSelection"
              class="mt-2 text-sm text-red-500 hover:text-red-600"
            >
              선택 해제
            </button>
          </div>

          <!-- 지급 금액 -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-slate-700 mb-2">지급 포인트</label>
            <div class="relative">
              <input
                type="number"
                v-model.number="giveAmount"
                placeholder="0"
                min="1"
                class="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20 transition pr-8"
              />
              <span class="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">P</span>
            </div>
            <!-- 빠른 선택 버튼 -->
            <div class="flex flex-wrap gap-2 mt-2">
              <button
                v-for="amount in quickAmounts"
                :key="amount"
                @click="giveAmount = amount"
                class="px-3 py-1.5 text-sm bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition"
              >
                {{ amount.toLocaleString() }}P
              </button>
            </div>
          </div>

          <!-- 지급 사유 -->
          <div class="mb-6">
            <label class="block text-sm font-medium text-slate-700 mb-2">지급 사유 (선택)</label>
            <input
              type="text"
              v-model="giveDescription"
              placeholder="예: 이벤트 보상, 오류 보상 등"
              class="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20 transition"
            />
          </div>

          <!-- 지급 버튼 -->
          <button
            @click="handleGivePoint"
            :disabled="!canGive || processing"
            class="w-full py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ processing ? '처리 중...' : '포인트 지급하기' }}
          </button>
        </div>

        <!-- 최근 지급 내역 -->
        <div class="bg-white rounded-xl border border-slate-200 p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-bold text-slate-800">최근 지급 내역</h2>
            <button
              @click="loadRecentGives"
              class="text-sm text-green-600 hover:text-green-700"
            >
              새로고침
            </button>
          </div>

          <div v-if="loadingRecent" class="flex justify-center py-8">
            <div class="w-6 h-6 border-2 border-green-600/30 border-t-green-600 rounded-full animate-spin"></div>
          </div>

          <div v-else-if="recentGives.length === 0" class="text-center py-8 text-slate-400">
            지급 내역이 없습니다
          </div>

          <div v-else class="space-y-3">
            <div
              v-for="give in recentGives"
              :key="give.id"
              class="p-4 bg-slate-50 rounded-xl"
            >
              <div class="flex items-center justify-between mb-1">
                <div class="flex items-center gap-2">
                  <span class="font-medium text-slate-800">{{ give.partnerDisplayName || give.partnerName || give.partnerId }}</span>
                  <span v-if="give.partnerDisplayName && give.partnerDisplayName !== give.partnerName" class="text-xs text-slate-400">({{ give.partnerName }})</span>
                </div>
                <span class="font-bold text-green-600">+{{ give.amount?.toLocaleString() }}P</span>
              </div>
              <p class="text-sm text-slate-500">{{ give.description || '관리자 포인트 지급' }}</p>
              <p class="text-xs text-slate-400 mt-1">{{ formatDate(give.createdAt) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { collection, query as firestoreQuery, where, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'
import { usePointChargeAdmin } from '@/composables/admin/usePointChargeAdmin'

export default {
  name: 'PointGiveView',
  components: {
    AdminLayout
  },
  setup() {
    const { givePointToPartner, getPartnerList, getPartnerPoint } = usePointChargeAdmin()

    const partners = ref([])
    const searchQuery = ref('')
    const showDropdown = ref(false)
    const selectedPartner = ref(null)
    const selectedPartnerPoint = ref(null)
    const giveAmount = ref(null)
    const giveDescription = ref('')
    const processing = ref(false)
    const recentGives = ref([])
    const loadingRecent = ref(false)

    const quickAmounts = [1000, 5000, 10000, 50000, 100000]

    // 필터된 파트너 목록
    const filteredPartners = computed(() => {
      if (!searchQuery.value) return partners.value.slice(0, 10)
      const q = searchQuery.value.toLowerCase()
      return partners.value.filter(p =>
        (p.name?.toLowerCase().includes(q)) ||
        (p.displayName?.toLowerCase().includes(q)) ||
        (p.email?.toLowerCase().includes(q))
      ).slice(0, 10)
    })

    // 지급 가능 여부
    const canGive = computed(() => {
      return selectedPartner.value && giveAmount.value && giveAmount.value > 0
    })

    // 파트너 선택
    const selectPartner = async (partner) => {
      selectedPartner.value = partner
      searchQuery.value = partner.displayName || partner.name || partner.email
      showDropdown.value = false

      // 선택한 파트너의 포인트 조회
      selectedPartnerPoint.value = await getPartnerPoint(partner.id)
    }

    // 선택 해제
    const clearSelection = () => {
      selectedPartner.value = null
      selectedPartnerPoint.value = null
      searchQuery.value = ''
    }

    // 포인트 지급
    const handleGivePoint = async () => {
      if (!canGive.value) return

      const partnerName = selectedPartner.value.displayName || selectedPartner.value.name || selectedPartner.value.email
      const confirmed = confirm(
        `${partnerName}님에게 ` +
        `${giveAmount.value.toLocaleString()}P를 지급하시겠습니까?`
      )
      if (!confirmed) return

      processing.value = true
      const result = await givePointToPartner(
        selectedPartner.value.id,
        giveAmount.value,
        giveDescription.value || `관리자 포인트 지급 ${giveAmount.value.toLocaleString()}P`
      )
      processing.value = false

      if (result.success) {
        alert(`포인트 지급 완료!\n새 잔액: ${result.balance.toLocaleString()}P`)
        selectedPartnerPoint.value = { ...selectedPartnerPoint.value, balance: result.balance }
        giveAmount.value = null
        giveDescription.value = ''
        await loadRecentGives()
      } else {
        alert('포인트 지급 실패: ' + result.error)
      }
    }

    // 최근 지급 내역 로드
    const loadRecentGives = async () => {
      loadingRecent.value = true
      try {
        const q = firestoreQuery(
          collection(db, 'pointTransactions'),
          where('type', '==', 'admin_give'),
          orderBy('createdAt', 'desc'),
          limit(20)
        )
        const snapshot = await getDocs(q)
        const gives = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        }))

        // 파트너 이름/영업명 가져오기
        for (const give of gives) {
          const userDoc = await getDoc(doc(db, 'users', give.partnerId))
          if (userDoc.exists()) {
            const userData = userDoc.data()
            give.partnerName = userData.name || userData.email

            // 영업명 조회
            try {
              const ppQuery = firestoreQuery(
                collection(db, 'propertyPartners'),
                where('partnerId', '==', give.partnerId),
                where('status', '==', 'active')
              )
              const ppSnapshot = await getDocs(ppQuery)
              if (!ppSnapshot.empty) {
                give.partnerDisplayName = ppSnapshot.docs[0].data().displayName
              }
            } catch {
              // 영업명 조회 실패 시 무시
            }
          }
        }

        recentGives.value = gives
      } catch (err) {
        console.error('최근 지급 내역 로드 실패:', err)
      } finally {
        loadingRecent.value = false
      }
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('ko-KR', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    // 드롭다운 외부 클릭 시 닫기
    const handleClickOutside = (e) => {
      if (!e.target.closest('.relative')) {
        showDropdown.value = false
      }
    }

    onMounted(async () => {
      partners.value = await getPartnerList()
      await loadRecentGives()
      document.addEventListener('click', handleClickOutside)
    })

    return {
      partners,
      searchQuery,
      showDropdown,
      selectedPartner,
      selectedPartnerPoint,
      giveAmount,
      giveDescription,
      processing,
      recentGives,
      loadingRecent,
      quickAmounts,
      filteredPartners,
      canGive,
      selectPartner,
      clearSelection,
      handleGivePoint,
      loadRecentGives,
      formatDate
    }
  }
}
</script>
